import {
  Folder,
  Dashboard,
  Groups,
  TableChartOutlined,
  ManageHistory,
  History,
  Celebration,
  AirplaneTicket,
  CloudUpload,
  CloudDownload,
  MoreTime,
  LogoutOutlined,
  AccessTime,
  PersonPin,
  Work,
  EventBusy,
  Badge,
  CarRepair,
  LocalShipping,
  Insights,
  CalendarMonth,
} from "@mui/icons-material";
import { Box, Tooltip, useTheme } from "@mui/material";
import { useContext, useState } from "react";
import {
  Sidebar,
  Menu,
  MenuItem,
  SubMenu,
  MenuItemStyles,
} from "react-pro-sidebar";
import { tokens } from "./AppStyles";
import { useLocation, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { useLazyLogoutQuery } from "../data/endpoints/app.endpoints";
import { selectLoggedUser } from "../data/store/authSlice";
import { RootContext } from "../context/RootContext";
import { useDispatch } from "react-redux";
import { UserRole } from "../utils/enums/user-role.enum";
import AlertDialog from "../shared/modal/AlertDialog";
import { SidebarHeader } from "./sidebar/SidenavHeader";
import useIsMobile from "../shared/hooks/useIsMobile";
import { useTranslation } from "react-i18next";
export const SideNav: React.FC = () => {
  const { pathname } = useLocation();
  const navigation = useNavigate();
  let [collapsed, setCollapsed] = useState<boolean>(false);
  const [selected, setSelected] = useState(pathname);
  const [openLogOutModal, setLogOutModal] = useState(false);
  const user = useSelector(selectLoggedUser);
  const [logoutUser] = useLazyLogoutQuery();
  const { logoutAndClearState } = useContext(RootContext);
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const [openSubMenu, setOpenSubMenu] = useState<any>(
    getLastOpenSubMenuFromSession()
  );

  function getLastOpenSubMenuFromSession() {
    return sessionStorage.getItem("lastOpenSubMenu") || null;
  }

  const handleSubMenuClick = (name: any) => {
    setOpenSubMenu(name === openSubMenu ? null : name);
    setLastOpenSubMenuToSession(name === openSubMenu ? null : name);
  };

  function setLastOpenSubMenuToSession(submenu: any) {
    sessionStorage.setItem("lastOpenSubMenu", submenu);
  }

  const handleModalOpen = () => {
    setLogOutModal(true);
  };

  const handleItemClick = (to: any) => {
    setSelected(to);
    navigation(to);
  };

  const menuItemStyles: MenuItemStyles = {
    subMenuContent: {
      backgroundColor: colors.component,
    },
    button: ({ active }) => {
      if (active) {
        return {
          backgroundColor: colors.sideNav,
          borderRadius: "0 10px 10px 0",
          transition: "ease-in-out 0.2s",
          "&:hover": {
            backgroundColor: "#b7dbff80",
          },
        };
      }
      return {
        borderRadius: "0 10px 10px 0",
        "&:hover": {
          backgroundColor: "rgba(183, 219, 255, 0.2)",
          transition: "ease-in-out 0.2s",
        },
      };
    },
    root: {
      margin: "0 0 5px 0",
      borderRadius: "0 10px 10px 0",
      width: "90%",
      fontSize: "15px",
      fontWeight: 400,
      backgroundColor: colors.component,
      border: "none",
    },
    SubMenuExpandIcon: {
      color: "#b6b7b9",
    },
    label: ({ open }) => ({
      fontWeight: open ? 600 : undefined,
    }),
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100% auto",
        boxShadow: `${colors.shadow} 0px 6px 24px 0px, ${colors.shadow} 0px 0px 0px 1px`,
        backgroundColor: colors.component,
      }}
    >
      <Sidebar
        collapsed={isMobile ? (collapsed = true) : collapsed}
        style={{
          border: "none",
          backgroundColor: colors.component,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            backgroundColor: colors.component,
          }}
        >
          <SidebarHeader
            handleItemClick={handleItemClick}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
          />
          <div
            style={{
              flex: 1,
              marginBottom: "32px",
              marginTop: "75px",
              backgroundColor: colors.component,
            }}
          >
            <Menu menuItemStyles={menuItemStyles}>
              <MenuItem
                active={selected === "/dashboard"}
                onClick={() => handleItemClick("/dashboard")}
                icon={<Dashboard />}
              >
                {t("SIDENAV.DASHBOARD")}
              </MenuItem>
              {user?.role === UserRole.ADMIN ? (
                <>
                  <MenuItem
                    active={selected === "/employees"}
                    onClick={() => handleItemClick("/employees")}
                    icon={<Groups />}
                  >
                    {t("SIDENAV.USERS")}
                  </MenuItem>
                  <SubMenu
                    onClick={() => handleSubMenuClick("Vehicle")}
                    open={openSubMenu === "Vehicle"}
                    label={t("SIDENAV.VEHICLE")}
                    icon={<LocalShipping />}
                  >
                    <MenuItem
                      active={selected === "/vehicles"}
                      onClick={() => handleItemClick("/vehicles")}
                      icon={<CarRepair />}
                    >
                      {t("SIDENAV.VEHICLES")}
                    </MenuItem>
                    <Tooltip
                      placement="right"
                      title={t("SIDENAV.VEHICLE_INSIGHTS")}
                    >
                      <MenuItem
                        active={selected === "/vehicle-insights"}
                        onClick={() => handleItemClick("/vehicle-insights")}
                        icon={<Insights />}
                      >
                        {t("SIDENAV.VEHICLE_INSIGHTS")}
                      </MenuItem>
                    </Tooltip>
                  </SubMenu>
                  <MenuItem
                    active={selected === "/activity"}
                    onClick={() => handleItemClick("/activity")}
                    icon={<TableChartOutlined />}
                  >
                    {t("SIDENAV.ACTIVITY")}
                  </MenuItem>
                  <SubMenu
                    onClick={() => handleSubMenuClick("Timetracker")}
                    open={openSubMenu === "Timetracker"}
                    label={t("SIDENAV.TIME_TRACKER")}
                    icon={<AccessTime />}
                  >
                    <Tooltip
                      placement="right"
                      title={t("SIDENAV.MANAGE_TIME_TRACKERS")}
                    >
                      <Box>
                        <MenuItem
                          active={selected === "/manage-time-trackers"}
                          onClick={() =>
                            handleItemClick("/manage-time-trackers")
                          }
                          icon={<ManageHistory />}
                        >
                          {t("SIDENAV.MANAGE_TIME_TRACKERS")}
                        </MenuItem>
                      </Box>
                    </Tooltip>
                    <Tooltip
                      placement="right"
                      title={t("SIDENAV.VIEW_TIME_TRACKERS")}
                    >
                      <Box>
                        <MenuItem
                          active={selected === "/view-time-trackers"}
                          onClick={() => handleItemClick("/view-time-trackers")}
                          icon={<History />}
                        >
                          {t("SIDENAV.VIEW_TIME_TRACKERS")}
                        </MenuItem>
                      </Box>
                    </Tooltip>
                  </SubMenu>
                  <SubMenu
                    onClick={() => handleSubMenuClick("Timeoff")}
                    open={openSubMenu === "Timeoff"}
                    label={t("SIDENAV.ABSENCE_MANAGEMENT")}
                    icon={<Badge />}
                  >
                    {!isMobile && (
                      <MenuItem
                        active={selected === "/overview-calendar"}
                        onClick={() => handleItemClick("/overview-calendar")}
                        icon={<CalendarMonth />}
                      >
                        Calendar
                      </MenuItem>
                    )}
                    <Tooltip
                      placement="right"
                      title={t("SIDENAV.LEAVE_REQUEST")}
                    >
                      <Box>
                        <MenuItem
                          active={selected === "/leave-requests"}
                          onClick={() => handleItemClick("/leave-requests")}
                          icon={<AirplaneTicket />}
                        >
                          {t("SIDENAV.LEAVE_REQUEST")}
                        </MenuItem>
                      </Box>
                    </Tooltip>
                    <Tooltip
                      placement="right"
                      title={t("SIDENAV.ABSENCE_TODAY")}
                    >
                      <Box>
                        <MenuItem
                          active={selected === "/absents"}
                          onClick={() => handleItemClick("/absents")}
                          icon={<EventBusy />}
                        >
                          {t("SIDENAV.ABSENCE_TODAY")}
                        </MenuItem>
                      </Box>
                    </Tooltip>
                    <Tooltip
                      placement="right"
                      title={t("SIDENAV.NATIONAL_HOLIDAYS")}
                    >
                      <Box>
                        <MenuItem
                          active={selected === "/holidays"}
                          onClick={() => handleItemClick("/holidays")}
                          icon={<Celebration />}
                        >
                          {t("SIDENAV.NATIONAL_HOLIDAYS")}
                        </MenuItem>
                      </Box>
                    </Tooltip>
                  </SubMenu>
                  <MenuItem
                    active={selected === "/file-manager"}
                    onClick={() => handleItemClick("/file-manager")}
                    icon={<Folder />}
                  >
                    {t("SIDENAV.FILE_MANAGER")}
                  </MenuItem>
                </>
              ) : user?.role === UserRole.EMPLOYEE ? (
                <>
                  <MenuItem
                    active={selected === "/time-tracker"}
                    onClick={() => handleItemClick("/time-tracker")}
                    icon={<MoreTime />}
                  >
                    {t("SIDENAV.TIME_TRACKER")}
                  </MenuItem>
                  <MenuItem
                    active={selected === "/my-work-time"}
                    onClick={() => handleItemClick("/my-work-time")}
                    icon={<Work />}
                  >
                    {t("SIDENAV.MY_WORK_TIME")}
                  </MenuItem>
                  <MenuItem
                    active={selected === "/my-profile"}
                    onClick={() => handleItemClick("/my-profile")}
                    icon={<PersonPin />}
                  >
                    {t("SIDENAV.MY_PROFILE")}{" "}
                  </MenuItem>
                  <MenuItem
                    active={selected === "/leave-request"}
                    onClick={() => handleItemClick("/leave-request")}
                    icon={<AirplaneTicket />}
                  >
                    {t("SIDENAV.LEAVE_REQUEST")}
                  </MenuItem>
                  <MenuItem
                    active={selected === "/file-manager"}
                    onClick={() => handleItemClick("/file-manager")}
                    icon={<Folder />}
                  >
                    {t("SIDENAV.FILE_MANAGER")}
                  </MenuItem>
                </>
              ) : null}
              <AlertDialog
                openModal={openLogOutModal}
                setOpenModal={setLogOutModal}
                buttonTitle=""
                handleSubmitDialog={() => {
                  logoutUser(undefined).then(() => {
                    logoutAndClearState(navigation, dispatch);
                  });
                }}
                ButtonComponent={(props: any) => (
                  <MenuItem onClick={handleModalOpen} icon={<LogoutOutlined />}>
                    {t("SIDENAV.LOGOUT")}
                  </MenuItem>
                )}
                mt="20px"
              >
                {t("LOGOUT_MODAL_TEXT")}
              </AlertDialog>
            </Menu>
          </div>
        </div>
      </Sidebar>
    </Box>
  );
};
